import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

/**
 * One confirmation for every action that cannot be taken back: removing a
 * contact, deleting a role, freezing a charge sheet. A shared shop
 * workstation with a shared mouse is where single-click deletes get found.
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Remove Pat?', body: '…', confirmLabel: 'Remove', destructive: true })) …
 *
 * `body` may be a node, so a confirmation can restate what is about to be
 * committed — the totals of the sheet being approved, say.
 */
export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button in the error colour. */
  destructive?: boolean;
}

type Ask = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Ask | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const ask = useCallback<Ask>((next) => {
    // A second question while one is open answers the first with "no".
    resolver.current?.(false);
    setOptions(next);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const settle = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOptions(null);
  };

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <Dialog open={!!options} onClose={() => settle(false)} maxWidth="xs" fullWidth data-testid="confirm-dialog">
        {options && (
          <>
            <DialogTitle>{options.title}</DialogTitle>
            {options.body && (
              <DialogContent>
                {typeof options.body === 'string'
                  ? <DialogContentText>{options.body}</DialogContentText>
                  : options.body}
                {options.destructive && <Alert severity="warning" sx={{ mt: 2 }}>This cannot be undone.</Alert>}
              </DialogContent>
            )}
            <DialogActions>
              <Button onClick={() => settle(false)} data-testid="confirm-cancel">{options.cancelLabel ?? 'Cancel'}</Button>
              <Button
                variant="contained" color={options.destructive ? 'error' : 'primary'} autoFocus
                onClick={() => settle(true)} data-testid="confirm-ok"
              >
                {options.confirmLabel ?? 'Confirm'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext);
  if (!ask) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ask;
}
